import { Component } from '@theme/component';

/**
 * <caldera-step-media>
 *
 * Thumbnail-tabbed media gallery used by the caldera-how-to-use section.
 * Each step block exposes its metadata via `data-*` attributes (id, label,
 * thumbnail image URL, panel id, video flag). This component hydrates the
 * empty [ref="tablist"] container with a generated thumbnail rail and
 * drives tab activation: the newly-active step's visible video auto-plays,
 * every other step's videos pause and rewind.
 *
 * Play/pause and mute/unmute controls are owned by the nested
 * `<caldera-video>` component inside each step. This element only drives
 * playback lifecycle via the native `<video>` API; the controls observe
 * those events and update their button state automatically.
 *
 * @typedef {object} CalderaStepMediaRefs
 * @property {HTMLElement} tablist - Container that receives generated thumb buttons.
 * @property {HTMLElement[]} panel - Tab panels, one per step block.
 * @property {HTMLButtonElement[]} [thumb] - Generated thumb buttons (populated after build).
 *
 * @extends {Component<CalderaStepMediaRefs>}
 */
class CalderaStepMedia extends Component {
  requiredRefs = ['tablist', 'panel'];

  /** @type {string} */
  #classPrefix = 'caldera-step-media';

  /** @type {boolean} */
  #reducedMotion = false;

  connectedCallback() {
    super.connectedCallback();

    this.#classPrefix = this.dataset.classPrefix || 'caldera-step-media';
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const blockEls = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-tab-id]'))
    );

    if (blockEls.length === 0) return;

    this.#buildThumbs(blockEls);

    // Generated thumbs carry `ref="thumb[]"`, so re-scan refs so the framework
    // picks them up (the initial scan in super.connectedCallback ran before
    // they existed).
    this.updatedCallback();

    this.#playActiveVideo();
  }

  /**
   * @param {HTMLElement[]} blockEls
   */
  #buildThumbs(blockEls) {
    const fragment = document.createDocumentFragment();
    const prefix = this.#classPrefix;

    for (const [index, blockEl] of blockEls.entries()) {
      const tabId = blockEl.dataset.tabId;
      const buttonId = blockEl.dataset.tabButtonId;
      const panelId = blockEl.dataset.tabPanelId;
      const label = blockEl.dataset.tabLabel || '';
      const thumbUrl = blockEl.dataset.tabThumb || '';
      if (!tabId || !buttonId) continue;

      const isFirst = index === 0;
      const button = document.createElement('button');
      button.type = 'button';
      button.id = buttonId;
      button.className = `${prefix}__thumb`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      if (panelId) button.setAttribute('aria-controls', panelId);
      button.tabIndex = isFirst ? 0 : -1;
      button.dataset.tabTarget = tabId;
      button.setAttribute('aria-label', label);
      button.setAttribute('ref', 'thumb[]');
      button.setAttribute('on:click', '/handleThumbClick');
      button.setAttribute('on:keydown', '/handleThumbKeydown');

      if (thumbUrl) {
        const img = document.createElement('img');
        img.className = `${prefix}__thumb-image`;
        img.src = thumbUrl;
        img.alt = '';
        img.loading = 'lazy';
        button.appendChild(img);
      }

      const labelSpan = document.createElement('span');
      labelSpan.className = `${prefix}__thumb-label`;
      labelSpan.textContent = label;
      button.appendChild(labelSpan);

      fragment.appendChild(button);
    }

    this.refs.tablist.appendChild(fragment);
  }

  /** @param {Event} event */
  handleThumbClick = (event) => {
    const thumb = /** @type {HTMLButtonElement} */ (event.target);
    this.#activate(thumb);
  };

  /** @param {KeyboardEvent} event */
  handleThumbKeydown = (event) => {
    const thumbs = this.refs.thumb ?? [];
    const currentIndex = thumbs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (currentIndex === -1) return;

    /** @type {number | null} */
    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % thumbs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + thumbs.length) % thumbs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = thumbs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextThumb = thumbs[nextIndex];
    if (!nextThumb) return;
    this.#activate(nextThumb);
    nextThumb.focus();
  };

  /** @param {HTMLButtonElement} activeThumb */
  #activate(activeThumb) {
    const targetId = activeThumb.dataset.tabTarget;
    if (!targetId) return;

    const panelActiveClass = `${this.#classPrefix}__panel--active`;
    const thumbs = this.refs.thumb ?? [];

    for (const thumb of thumbs) {
      const isActive = thumb === activeThumb;
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      thumb.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of this.refs.panel) {
      const isActive = panel.dataset.tabPanel === targetId;
      panel.toggleAttribute('hidden', !isActive);
      panel.classList.toggle(panelActiveClass, isActive);

      if (!isActive) {
        const videos = panel.querySelectorAll('video[data-video]');
        for (const video of videos) {
          if (!(video instanceof HTMLVideoElement)) continue;
          video.pause();
          video.currentTime = 0;
        }
      }
    }

    this.#playActiveVideo();

    this.dispatchEvent(
      new CustomEvent('caldera-step-media:activate', {
        detail: { tabId: targetId, thumb: activeThumb },
        bubbles: true,
      })
    );
  }

  /** @returns {HTMLElement | null} */
  #getActivePanel() {
    return this.refs.panel.find((panel) => !panel.hasAttribute('hidden')) || null;
  }

  /**
   * Picks the currently visible video — desktop or mobile depending on
   * media query.
   *
   * @param {HTMLElement} panel
   * @returns {HTMLVideoElement | null}
   */
  #getVisibleVideo(panel) {
    const videos = /** @type {HTMLVideoElement[]} */ (Array.from(panel.querySelectorAll('video[data-video]')));
    return videos.find((video) => video.offsetParent !== null) || videos[0] || null;
  }

  #playActiveVideo() {
    if (this.#reducedMotion) return;
    const panel = this.#getActivePanel();
    if (!panel) return;
    const video = this.#getVisibleVideo(panel);
    if (!video) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      // Autoplay can reject on some browsers; the visible poster is the fallback.
      playPromise.catch(() => {});
    }
  }
}

if (!customElements.get('caldera-step-media')) {
  customElements.define('caldera-step-media', CalderaStepMedia);
}
